import style from "./Address.module.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, FieldError, useForm } from "react-hook-form";

import {
  Button,
  FieldGroup,
  FieldRow,
  FieldSet,
  Input,
  Section,
  SelectBox,
} from "react-corner-ds";
import i18n from "../../../i18n";
import {
  CountriesResponse,
  CountryItem,
} from "../../../models/response/response.model";
import { AddressInfo } from "../../../models/request/data/data.model";
import { definePatternByName } from "../../../utils/constants";
import { Request } from "../../../models/request/request.model";
import { store } from "../../../redux/store";
import { useFlow } from "../../../hooks/useFlow/useFlow";
import { Bank } from "../../../models/bank/bank.model";
import { Customer } from "../../../models/bank/customer/customer.model";

interface IAddress {
  modalRecap?: boolean;
  modalCallback?: () => void;
  mockObject?: Bank;
}

const Address: React.FC<IAddress> = ({
  modalRecap,
  modalCallback,
  mockObject,
}) => {
  const { callSteploader, getCitiesByZip } = useFlow();
  const [nextStepLoading, setNextStepLoading] = useState(false);
  const { t } = useTranslation();
  const [cityList, setCityList] = useState<{ label: string; value: string }[]>(
    []
  );
  const [isValidCity, setIsValidCity] = useState<boolean | undefined>(
    undefined
  );
  const [isValidStreet, setIsValidStreet] = useState<boolean>(true);
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [isChResident, setIsChResident] = useState<boolean>(false);
  const [isValidZipcode, setIsValidZipcode] = useState<boolean>(true);
  const [isCrossSetCity, setCrossSetCity] = useState<boolean>(false);

  const submitHandler = async (data: AddressInfo) => {
    let body: Request = {
      Data: {
        FlowDirection: modalRecap ? "Still" : "Next",
        Bank: {
          Customer: {
            Addresses: [
              {
                Country: data.country,
                City: data.city,
                ZipCode: isChResident ? data.zipCode_CH : data.zipCode,
                StreetNumber: data.streetNumber,
                Street: data.street,
                Type: "Home",
              },
            ],
          },
        },
      },
      Extra: {},
    };

    setNextStepLoading(true);
    if (modalRecap) {
      await callSteploader(body, false, "Address");
      setNextStepLoading(false);
    } else {
      await callSteploader(body);
    }
    modalRecap && modalCallback && modalCallback();
  };

  const items = useMemo(() => {
    return countries.map((item: CountryItem) => ({
      label: [item.name],
      value: item.alpha_2,
      icon: item.alpha_2,
    }));
  }, [countries]);

  useEffect(() => {
    import(`../../../assets/countries/${i18n.resolvedLanguage}.json`).then(
      (resp: CountriesResponse) => {
        setCountries(resp.countries);
      }
    );
  }, [i18n.resolvedLanguage]);

  const { handleSubmit, formState, control, setValue, trigger } =
    useForm<AddressInfo>({
      mode: "all",
      reValidateMode: "onChange",
      defaultValues: {
        country: "",
        city: "",
        zipCode: "",
        zipCode_CH: "",
        streetNumber: "",
        street: "",
      },
    });

  useEffect(() => {
    if (formState.errors.zipCode || formState.errors.city) {
      setIsValidZipcode(false);
    } else setIsValidZipcode(true);

    if (formState.errors.street || formState.errors.streetNumber) {
      setIsValidStreet(false);
    } else setIsValidStreet(true);
  }, [formState]);

  const validity = useCallback(
    (error: FieldError | undefined, isTouched: boolean) =>
      !error ? true : undefined,
    []
  );

  const validityCombine = useCallback(
    (
      error: FieldError | undefined,
      errorMsg: string | undefined,
      isTouched: boolean
    ) => (!error && !errorMsg && cityList.length > 0 ? true : undefined),
    [cityList.length]
  );

  const updateCityList = useCallback(
    async (zip: string, clearField: boolean) => {
      if (zip.length !== 4) {
        setCityList([]);
      } else {
        let resp = await getCitiesByZip(zip);
        let cities: string[] = resp && resp.data ? resp.data : [];
        let selectableCities: { label: string; value: string }[] = cities.map(
          (city: string) => ({
            label:
              city.toLowerCase().charAt(0).toUpperCase() +
              city.toLowerCase().slice(1),
            value:
              city.toLowerCase().charAt(0).toUpperCase() +
              city.toLowerCase().slice(1),
          })
        );
        setCityList(selectableCities);
        clearField && setValue("city", "");
      }
    },
    [getCitiesByZip, setValue]
  );

  const updateOnFocus = useCallback(() => {
    setValue("city", "");
    setIsValidCity(false);
  }, [setValue]);

  useEffect(() => {
    let customerStore: Customer = mockObject?.Customer ?? {};

    if (store.getState().appConf.workflowEnabled) {
      customerStore = store.getState().pega.Data?.Bank.Customer ?? {};
    }

    if (customerStore && customerStore.Addresses) {
      setValue("country", customerStore.Addresses[0].Country ?? "");
      setValue("zipCode", customerStore.Addresses[0].ZipCode ?? "");
      setValue("zipCode_CH", customerStore.Addresses[0].ZipCode ?? "");

      if (
        customerStore.Addresses[0].Country === "CH" &&
        customerStore.Addresses[0].ZipCode
      )
        updateCityList(
          customerStore.Addresses[0].ZipCode,
          customerStore.Addresses[0].City === ""
        );

      setValue(
        "city",
        customerStore.Addresses[0].City
          ? customerStore.Addresses[0].City.toLowerCase()
              .charAt(0)
              .toUpperCase() +
              customerStore.Addresses[0].City.toLowerCase().slice(1)
          : ""
      );

      store.getState().appConf.crossSelling && setCrossSetCity(true);

      setValue("street", customerStore.Addresses[0].Street ?? "");
      setValue("streetNumber", customerStore.Addresses[0].StreetNumber ?? "");

      //Trigger validation if there's customer data coming from pega
      if (customerStore.Addresses[0].Country)
        setTimeout(() => {
          trigger();
        }, 250);
    }
  }, [mockObject?.Customer, setValue, updateCityList, trigger]);

  const countrySelectBox = useMemo(
    () => (
      <Controller
        control={control}
        name={"country"}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({
          field: { onChange, name, value, onBlur },
          fieldState: { error },
        }) => (
          <SelectBox
            label={t`screens.address.country`}
            customCssClass={
              store.getState().pega?.Data?.Bank?.Customer?.KYC
                ? style.disabled
                : ""
            }
            items={items}
            name={name}
            value={value}
            onChange={(item: any) => {
              onChange(item.value);

              setIsChResident(item.value === "CH");
              setValue("city", "");
              setValue("zipCode", "");
              setValue("zipCode_CH", "");
              setCityList([]);
            }}
            onBlur={onBlur}
            invalid={error?.message ? true : false}
            errorMsg={error?.message}
            disabled={store.getState().pega?.Data?.Bank?.Customer?.KYC}
          />
        )}
      />
    ),
    [control, items, setValue, t]
  );

  const buildZipCodeChInput = useCallback(
    (errorMsg?: string) => (
      <Controller
        control={control}
        name={"zipCode_CH"}
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("ZipCode"),
            message: t`errors.input.invalidPattern`,
          },
          validate: () => (cityList.length > 0 ? true : false),
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            name={name}
            type="text"
            cssClass="prefixWithLabel"
            value={value}
            label={t`screens.address.zipCode`}
            onBlur={onBlur}
            disabled={store.getState().pega?.Data?.Bank?.Customer?.KYC}
            valid={
              error || errorMsg || isValidCity === false
                ? false
                : validityCombine(error, errorMsg, isTouched)
            }
            onFocus={() => updateOnFocus()}
            onChange={(e) => {
              onChange(e.target.value);
              updateCityList(e.target.value, true);
            }}
            errorMsg={
              error?.message ??
              errorMsg ??
              (cityList.length <= 0
                ? t`screens.common.chZipRequired`
                : t`errors.input.required`)
            }
            maxLength={4}
            customCssClass={style.customChResidentZipCode}
          />
        )}
      />
    ),
    [
      cityList.length,
      control,
      isValidCity,
      t,
      updateCityList,
      updateOnFocus,
      validityCombine,
    ]
  );

  const buildCitySelectBox = useMemo(
    () => (
      <Controller
        control={control}
        name={"city"}
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({
          field: { onChange, name, value, onBlur },
          fieldState: { error },
        }) => (
          <SelectBox
            name={name}
            customCssClass={style.customChResidentCity}
            label={t`screens.address.city`}
            cssClass="prefixedWithLabel"
            onBlur={onBlur}
            onChange={(item: any) => {
              onChange(item.value);
              setIsValidCity(true);
            }}
            disabled={
              store.getState().pega?.Data?.Bank?.Customer?.KYC && isCrossSetCity
            }
            value={value}
            items={cityList}
            invalid={error || isValidCity === false ? true : false}
          >
            {buildZipCodeChInput(error?.message)}
          </SelectBox>
        )}
      />
    ),
    [control, t, cityList, isValidCity, buildZipCodeChInput, isCrossSetCity]
  );

  const buildZipCodeInput = useCallback(
    (errorMsg?: string) => (
      <Controller
        control={control}
        name={"zipCode"}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("ZipCode"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            cssClass="prefixWithLabel"
            customCssClass={style.customPrefixZipCode}
            name={name}
            disabled={store.getState().pega?.Data?.Bank?.Customer?.KYC}
            label={t`screens.address.zipCode`}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            valid={error ? false : validity(error, isTouched)}
            errorMsg={errorMsg ?? error?.message}
            maxLength={5}
            type="text"
          />
        )}
      />
    ),
    [control, t, validity]
  );

  const buildCityInput = useMemo(
    () => (
      <Controller
        control={control}
        name={"city"}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("City"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { error },
        }) => (
          <Input
            name={name}
            label={t`screens.address.city`}
            cssClass="prefixedWithLabel"
            customCssClass={
              store.getState().pega?.Data?.Bank?.Customer?.KYC
                ? style.disabled
                : ""
            }
            value={value}
            disabled={
              store.getState().pega?.Data?.Bank?.Customer?.KYC && isCrossSetCity
            }
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            valid={isValidZipcode}
            maxLength={60}
            type="text"
          >
            {buildZipCodeInput(error?.message)}
          </Input>
        )}
      />
    ),
    [control, t, isValidZipcode, buildZipCodeInput, isCrossSetCity]
  );

  const buildStreetNumberInput = useCallback(
    (errorMsg?: string) => (
      <Controller
        control={control}
        name="street"
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("StreetNumber"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            cssClass="prefixWithLabel"
            customCssClass={style.customPrefixStreet}
            name={name}
            label={t`screens.address.street`}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            valid={error ? false : validity(error, isTouched)}
            errorMsg={errorMsg ?? error?.message}
            maxLength={80}
            type="text"
          />
        )}
      />
    ),
    [control, t, validity]
  );

  const buildCrossStreet = useCallback(
    (errorMsg?: string) => (
      <Controller
        control={control}
        name="street"
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("StreetNumber"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            customCssClass={
              store.getState().pega?.Data?.Bank?.Customer?.KYC
                ? style.disabled
                : ""
            }
            name={name}
            label={t`screens.address.street`}
            value={value}
            disabled={store.getState().pega?.Data?.Bank?.Customer?.KYC}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            valid={error ? false : validity(error, isTouched)}
            errorMsg={errorMsg ?? error?.message}
            maxLength={60}
            type="text"
          />
        )}
      />
    ),
    [control, t, validity]
  );

  const buildStreetInput = useCallback(
    () => (
      <Controller
        control={control}
        name="streetNumber"
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("Street"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { error },
        }) => (
          <Input
            name={name}
            label={t`screens.address.streetNumber`}
            cssClass="prefixedWithLabel"
            customCssClass={style.customPrefixedStreet}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            valid={isValidStreet}
            maxLength={60}
            type="text"
          >
            {buildStreetNumberInput(error?.message)}
          </Input>
        )}
      />
    ),
    [buildStreetNumberInput, control, isValidStreet, t]
  );

  return (
    <div className="container">
      <h1>{t`screens.address.pageTitle`}</h1>
      <h2>{t`screens.address.pageSubTitle`}</h2>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(submitHandler)}
        className={modalRecap ? style.modalView : ""}
      >
        <Section>
          <FieldSet>
            <FieldRow>
              <FieldGroup
                customCssClass={`${style.resCountry} ${
                  modalRecap || store.getState().pega?.Data?.Bank?.Customer?.KYC
                    ? style.disabledField
                    : ""
                }`}
              >
                {countrySelectBox}
              </FieldGroup>
            </FieldRow>
            <FieldRow>
              {isChResident ? (
                <FieldGroup>{buildCitySelectBox}</FieldGroup>
              ) : (
                <FieldGroup>{buildCityInput}</FieldGroup>
              )}
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                {store.getState().appConf.crossSelling
                  ? buildCrossStreet()
                  : buildStreetInput()}
              </FieldGroup>
            </FieldRow>
          </FieldSet>
        </Section>

        <Section>
          <FieldSet>
            <div className={style.btnHolder + " btnHolder"}>
              <Button
                disabled={!formState.isValid || nextStepLoading}
                text={t`screens.common.continue`}
                type="submit"
                icon={
                  nextStepLoading
                    ? { icon: "loader", position: "right", spin: true }
                    : undefined
                }
              />
            </div>
          </FieldSet>
        </Section>
      </form>
    </div>
  );
};

export default Address;
